import { useTranslation } from 'react-i18next'
import { Eye, EyeOff, Bell, BellOff } from 'lucide-react'
import { Input } from '@/components/shadcn/input'
import { Label } from '@/components/shadcn/label'
import { Checkbox } from '@/components/shadcn/checkbox'
import { ModuleAssignmentFields } from '@/components/common/ModuleAssignmentFields'
import { IconColorPicker } from '@/components/common/IconColorPicker'
import { TranslateButton } from '@/components/common/translations/TranslateButton'
import type { StandardModuleFieldsController } from './useStandardServiceModuleFields'
import { ServiceModuleType } from '@/api/model'
import {SERVICE_MODULE_TYPES} from "@/pages/service-modules/grid-components/ServiceModuleCard.tsx";

interface Props {
  fields: StandardModuleFieldsController
  /** Used to derive the type-specific fallback icon shown in the icon picker. */
  moduleType?: ServiceModuleType
  autoTranslateAll?: boolean
}

/** Column 1 of the editor: the standard fields that are the same for every module type. */
export function StandardModuleFields({ fields, moduleType, autoTranslateAll }: Props) {
  const { t } = useTranslation('service-modules')

  // Derive the fallback icon name for the icon picker — tells the picker which
  // icon to display when nothing is set, and suppresses the reset button when
  // the active icon already matches the fallback.
  const fallbackIconName = SERVICE_MODULE_TYPES.find((e) => e.value === moduleType)?.iconName ?? null

  return (
    <>
      <div className="flex flex-col gap-1.5">
        <Label>{t('standardFields.name')}</Label>
        <div className="flex gap-2">
          <Input
            value={fields.name}
            onChange={(e) => fields.setName(e.target.value)}
            placeholder={t('standardFields.namePlaceholder')}
          />
          <TranslateButton
            dialogTitle={t('standardFields.nameTranslateTitle')}
            value={fields.nameTranslations}
            onChange={fields.setNameTranslations}
            plannedLanguages={fields.namePlannedLanguages}
            onPlannedChange={fields.setNamePlannedLanguages}
            autoTranslateAll={autoTranslateAll}
          />
        </div>
      </div>

      {/* Symbol: image OR icon+color. fallbackIconName drives the preview when no
          icon is set and controls reset-button visibility. */}
      <IconColorPicker
        iconName={fields.iconName}
        colorHex={fields.colorHex}
        iconUrl={fields.iconUrl}
        busy={fields.iconBusy}
        fallbackIconName={fallbackIconName}
        onIconChange={fields.setIconName}
        onColorChange={fields.setColorHex}
        onUpload={fields.uploadIcon}
        onDelete={fields.deleteIcon}
      />

      <div className="flex flex-col gap-2.5">
        <div className="flex items-center gap-2">
          <Checkbox id="visible" checked={fields.isVisible} onCheckedChange={(v) => fields.setIsVisible(!!v)} />
          <label htmlFor="visible" className="text-sm cursor-pointer flex items-center gap-1.5">
            {fields.isVisible ? <Eye className="size-3.5" /> : <EyeOff className="size-3.5" />}
            {t('standardFields.visible')}
          </label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox id="notify" checked={fields.sendNotification} onCheckedChange={(v) => fields.setSendNotification(!!v)} />
          <label htmlFor="notify" className="text-sm cursor-pointer flex items-center gap-1.5">
            {fields.sendNotification ? <Bell className="size-3.5" /> : <BellOff className="size-3.5" />}
            {t('standardFields.notify')}
          </label>
        </div>
      </div>

      <ModuleAssignmentFields
        isCompanyWide={fields.isCompanyWide}
        onCompanyWideChange={fields.setIsCompanyWide}
        selectedGroupIds={fields.selectedGroupIds}
        onGroupsChange={fields.setSelectedGroupIds}
      />
    </>
  )
}
